import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const AdminMarketsMediaPage = withListOverflow(MarketsMediaListOwner);
// src/features/admin/AdminMarketsMediaPage.jsx
//
// ADMIN-MEDIA-S1: Markets & Location IDs admin page.
//
// Three tabs:
//   Tab 1 — Markets:       4-column photo card grid over all markets records.
//   Tab 2 — Offices:       Sortable table of all locations with photo upload.
//   Tab 3 — Location IDs:  Read-only ID registry — one suggested next ID per
//                          city. No creation or mutation UI here; new IDs
//                          are minted only from the Add-location modal.
//
// Admin role only (enforced by caller in AdminPage.jsx).
// All writes are audited server-side via recordAudit().

import React, {
  useState, useEffect, useCallback, useRef,
} from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { ToastBanner } from "../../components/ToastBanner.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { getNextLocationIds } from "../../data/adminClient.js";
// ADMIN-OFFICE-PHOTO-UPLOAD-FIELD-MISMATCH: reuse the same upload client the
// Edit-location modal (PhotoPanel.jsx) uses, instead of a bespoke FormData +
// apiFetch call. Keeps the multipart field name ("photo") and endpoint in
// one place so this surface can't drift from the working one again.
import { uploadLocationPhoto } from "../../data/locationsClient.js";
import { ExternalIdsTab } from "./ExternalIdsTab.jsx";
import { matchesFoldedQuery } from "../../utils/textSearch.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const REGIONS     = ["AMER", "EMEA", "APAC"];
const STAGES      = ["operational", "dormant", "build", "closed", "vacated"];

// Title-cased display labels for stage values. Underlying stored values
// remain the raw lowercase strings below — this map is display-only, used
// everywhere a stage is rendered on this page (Offices badges, ID Registry
// Stage column, and Stage filter option labels).
const STAGE_LABELS = {
  operational: "Operational",
  dormant: "Dormant",
  build: "In-Build",
  closed: "Retired",
  vacated: "Vacated",
};

function getStageLabel(stage) {
  return STAGE_LABELS[stage] || stage;
}

// Shallow equality check for ConsolidatedFilter selection objects
// ({ [dimensionId]: string[] }), used to decide when to show a tab's
// standard "Clear" control.
function selectionsEqual(a, b) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    const av = [...(a?.[key] ?? [])].sort();
    const bv = [...(b?.[key] ?? [])].sort();
    if (av.length !== bv.length) return false;
    for (let i = 0; i < av.length; i += 1) {
      if (av[i] !== bv[i]) return false;
    }
  }
  return true;
}

// Standard "Clear" control shared by all four tabs on this page — matches
// the Offices LIST toolbar reference pattern: transparent text button,
// visible only when a search/filter is active, resets both to the tab's
// default.
function ClearButton({ onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.background = T.blueSoft; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      style={{
        background: "transparent",
        border: "1px solid transparent",
        borderRadius: 8,
        color: T.blue,
        fontSize: T.fsBase,
        fontWeight: T.fwBold,
        cursor: "pointer",
        padding: "7px 11px",
        fontFamily: "inherit",
      }}
    >
      Clear
    </button>
  );
}

const TAB_KEYS    = ["markets", "offices", "id-registry", "suggested-ids", "external-ids"];
const TAB_LABELS  = {
  markets: "Markets",
  offices: "Offices",
  "id-registry": "ID Registry",
  "suggested-ids": "Suggested IDs",
  "external-ids": "External Location IDs",
};

const EXTERNAL_LOCATION_SOURCES = new Set(["all", "envoy", "workday", "spacebase"]);

function readInitialTabState() {
  const fallback = { tab: "markets", source: "all" };
  try {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab !== "external-ids") return fallback;
    const source = params.get("source");
    return {
      tab,
      source: EXTERNAL_LOCATION_SOURCES.has(source) ? source : "all",
    };
  } catch {
    return fallback;
  }
}

// ─── apiFetch helper ─────────────────────────────────────────────────────────

async function apiFetch(url, opts = {}) {
  const isJson = !(opts.body instanceof FormData);
  const res = await fetch(url, {
    headers: isJson
      ? { "Content-Type": "application/json", ...(opts.headers || {}) }
      : opts.headers || {},
    ...opts,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ─── Sub-tab nav ─────────────────────────────────────────────────────────────

function TabBar({ active, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${T.border}`,
        marginBottom: 24,
      }}
    >
      {TAB_KEYS.map((key) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            type="button"
            data-testid={`markets-tab-${key}`}
            onClick={() => onChange(key)}
            style={{
              padding: "9px 18px",
              fontSize: T.fsBase,
              fontWeight: isActive ? T.fwSemibold : 500,
              color: isActive ? T.blue : T.mid,
              background: "transparent",
              border: "none",
              borderBottom: isActive ? `2px solid ${T.blue}` : "2px solid transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              marginBottom: -1,
            }}
          >
            {TAB_LABELS[key]}
          </button>
        );
      })}
    </div>
  );
}

// ─── Tab 1: Markets photo grid ────────────────────────────────────────────────

const MARKETS_FILTER_DIMENSIONS = [
  { id: "status", label: "Status", options: [
    { value: "active", label: "Active" },
    { value: "exited", label: "Exited" },
  ] },
];

// Default: Active only, Exited hidden until the user selects it.
const MARKETS_DEFAULT_SELECTIONS = { status: ["active"] };

function marketsViewFromSelections(selections) {
  const sel = selections.status ?? [];
  const hasActive = sel.includes("active");
  const hasExited = sel.includes("exited");
  if (hasActive && hasExited) return "all";
  if (hasExited) return "inactive";
  return "active";
}

function MarketCard({ market, onPhotoUpdate, onToggleActive }) {
  const [hovering, setHovering]         = useState(false);
  const [saving, setSaving]             = useState(false);
  const [err, setErr]                   = useState(null);
  const fileRef = useRef(null);

  const isActive = market.active !== false;

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const stored = await apiFetch(`/api/markets/${market.id}/photo`, {
        method: "POST",
        body: form,
        headers: {},
      });
      onPhotoUpdate(market.id, stored.url);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  }

  async function handleUrlInput() {
    const url = window.prompt("Enter photo URL:");
    if (!url || !url.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      await apiFetch(`/api/markets/${market.id}`, {
        method: "PATCH",
        body: JSON.stringify({ photoUrl: url.trim() }),
      });
      onPhotoUpdate(market.id, url.trim());
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive() {
    setSaving(true);
    setErr(null);
    try {
      await apiFetch(`/api/markets/${market.id}/active`, {
        method: "PATCH",
        body: JSON.stringify({ active: !isActive }),
      });
      onToggleActive(market.id);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid={`market-card-${market.id}`}
      style={{
        border: `0.5px solid ${T.border}`,
        borderRadius: 8,
        overflow: "hidden",
        background: T.card,
        display: "flex",
        flexDirection: "column",
        opacity: isActive ? 1 : 0.5,
      }}
    >
      {/* Photo area */}
      <div
        style={{
          position: "relative",
          height: 110,
          background: T.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          overflow: "hidden",
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={() => fileRef.current?.click()}
      >
        {market.photoUrl ? (
          <img
            src={market.photoUrl}
            alt={market.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div style={{ textAlign: "center", color: T.light }}>
            <div style={{ fontSize: T.fsXl6, marginBottom: 4 }}>📷</div>
            <div style={{ fontSize: T.fsXs }}>No photo</div>
          </div>
        )}
        {/* Inactive badge */}
        {!isActive && (
          <div
            style={{
              position: "absolute",
              top: 6,
              left: 6,
              background: "rgba(0,0,0,0.55)",
              color: T.white,
              fontSize: T.fsXs,
              fontWeight: T.fwSemibold,
              borderRadius: 4,
              padding: "1px 7px",
              letterSpacing: 0.3,
            }}
          >
            Inactive
          </div>
        )}
        {/* Hover overlay */}
        {hovering && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <span style={{ color: T.card, fontSize: T.fsSm, fontWeight: T.fwSemibold }}>
              {market.photoUrl ? "Change photo" : "Upload photo"}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleUrlInput(); }}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.4)",
                borderRadius: 4,
                color: T.card,
                fontSize: T.fsXs,
                padding: "2px 8px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              URL
            </button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      {/* Info */}
      <div style={{ padding: "10px 12px", flex: 1 }}>
        <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 3 }}>
          {market.name}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span
            style={{
              fontSize: T.fsXs,
              fontWeight: T.fwSemibold,
              color: T.blue,
              background: T.blueSoft,
              borderRadius: 4,
              padding: "1px 6px",
            }}
          >
            {market.region}
          </span>
          <span style={{ fontSize: T.fsXs, color: T.mid }}>
            {market.locationCount ?? 0} office{market.locationCount === 1 ? "" : "s"}
          </span>
        </div>
        {err && <div style={{ fontSize: T.fsXs, color: T.red, marginTop: 4 }}>{err}</div>}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "8px 12px",
          borderTop: `0.5px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", flex: 1 }}>
          <input
            type="checkbox"
            data-testid={`market-active-toggle-${market.id}`}
            checked={isActive}
            disabled={saving}
            onChange={handleToggleActive}
            style={{ accentColor: T.blue }}
          />
          <span style={{ fontSize: T.fsSm, color: T.mid }}>Active</span>
        </label>
        {saving && <span style={{ fontSize: T.fsXs, color: T.light }}>Saving…</span>}
      </div>
    </div>
  );
}

function MarketsTab() {
  const [markets, setMarkets]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selections, setSelections] = useState(MARKETS_DEFAULT_SELECTIONS);
  const [search, setSearch]         = useState("");

  const view = marketsViewFromSelections(selections);
  const isNonDefaultView = search !== "" || !selectionsEqual(selections, MARKETS_DEFAULT_SELECTIONS);

  const load = useCallback((v) => {
    setLoading(true);
    setError(null);
    apiFetch(`/api/markets?view=${v}`)
      .then((data) => {
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => a.name.localeCompare(b.name))
          : [];
        setMarkets(sorted);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(view); }, [load, view]);

  const handlePhotoUpdate = useCallback((marketId, photoUrl) => {
    setMarkets((prev) =>
      prev.map((m) => m.id === marketId ? { ...m, photoUrl } : m)
    );
  }, []);

  const handleToggleActive = useCallback(() => {
    load(view);
  }, [load, view]);

  function handleClear() {
    setSearch("");
    setSelections(MARKETS_DEFAULT_SELECTIONS);
  }

  // Search scoped to name/market/region — for market records the "market"
  // name and the record's own name are the same field, so we match name +
  // region.
  const filteredMarkets = markets.filter((m) => {
    const q = search.trim();
    if (!q) return true;
    return matchesFoldedQuery(q, m.name, m.region);
  });

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginRight: 4 }}>
          Market photos
        </div>

        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search name, market, or region…"
          testId="markets-search"
        />

        <ConsolidatedFilter
          dimensions={MARKETS_FILTER_DIMENSIONS}
          selections={selections}
          onChange={setSelections}
          testId="markets-filter-btn"
        />

        {isNonDefaultView && (
          <ClearButton onClick={handleClear} testId="markets-clear-btn" />
        )}

        <div style={{ marginLeft: "auto", fontSize: T.fsSm, color: T.mid }}>
          Click a card to upload a photo, or paste a URL.
        </div>
      </div>

      {loading && <div style={{ color: T.mid, fontSize: T.fsSm }}>Loading markets…</div>}
      {error   && <ErrorBanner error={error} />}

      {!loading && !error && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 14,
            }}
          >
            {filteredMarkets.map((m) => (
              <MarketCard
                key={m.id}
                market={m}
                onPhotoUpdate={handlePhotoUpdate}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
          {filteredMarkets.length === 0 && (
            <div style={{ color: T.light, textAlign: "center", padding: "40px 0", fontSize: T.fsBase }}>
              {search
                ? "No markets match the current search/filters."
                : view === "inactive"
                  ? "No exited markets."
                  : "No markets found. Add markets in the Plan section first."}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tab 2: Offices photo table ───────────────────────────────────────────────

const OFFICES_FILTER_DIMENSIONS = [
  { id: "region", label: "Region", options: REGIONS.map((r) => ({ value: r, label: r })) },
  { id: "stage",  label: "Stage",  options: STAGES.map((s) => ({ value: s, label: getStageLabel(s) })) },
];

// Default: Stage limited to Operational + In-Build; other stages hidden
// until selected. Region unfiltered.
const OFFICES_DEFAULT_SELECTIONS = { stage: ["operational", "build"] };

function OfficesTab() {
  const [locations, setLocations]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selections, setSelections] = useState(OFFICES_DEFAULT_SELECTIONS);
  const [search, setSearch]         = useState("");
  const [toast, setToast]           = useState(null);

  const isNonDefaultView = search !== "" || !selectionsEqual(selections, OFFICES_DEFAULT_SELECTIONS);

  useEffect(() => {
    apiFetch("/api/locations")
      .then((data) => setLocations(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function handleClear() {
    setSearch("");
    setSelections(OFFICES_DEFAULT_SELECTIONS);
  }

  const filtered = locations.filter((l) => {
    if (selections.region?.length > 0 && !selections.region.includes(l.region)) return false;
    if (selections.stage?.length  > 0 && !selections.stage.includes(l.stage))    return false;
    const q = search.trim();
    if (q) {
      if (!matchesFoldedQuery(q, l.locationAlias, l.market, l.region)) return false;
    }
    return true;
  });

  async function handleUploadPhoto(loc, file) {
    try {
      // ADMIN-OFFICE-PHOTO-UPLOAD-FIELD-MISMATCH: use the same shared
      // uploadLocationPhoto client as the Edit-location modal (PhotoPanel.jsx)
      // — same endpoint, same "photo" multipart field name — instead of a
      // separate hand-rolled FormData call that had drifted to "file" and
      // was rejected by the server's multer middleware with "Unexpected field".
      const updated = await uploadLocationPhoto(loc.id, file);
      if (updated?.imageUrl) {
        setLocations((prev) =>
          prev.map((l) => l.id === loc.id ? { ...l, imageUrl: updated.imageUrl } : l)
        );
        setToast(`Photo updated for ${loc.locationAlias || loc.id}`);
        setTimeout(() => setToast(null), 3000);
      }
    } catch (ex) {
      setError(ex.message);
    }
  }

  async function handleUrlUpdate(loc, url) {
    try {
      const updated = await apiFetch(`/api/locations/${loc.id}`, {
        method: "PATCH",
        body: JSON.stringify({ imageUrl: url }),
      });
      setLocations((prev) =>
        prev.map((l) => l.id === loc.id ? { ...l, imageUrl: updated?.imageUrl ?? url } : l)
      );
      setToast(`Photo updated for ${loc.locationAlias || loc.id}`);
      setTimeout(() => setToast(null), 3000);
    } catch (ex) {
      setError(ex.message);
    }
  }

  if (loading) return <div style={{ color: T.mid, fontSize: T.fsSm }}>Loading offices…</div>;
  if (error)   return <ErrorBanner error={error} />;

  return (
    <div>
      {toast && <ToastBanner message={toast} />}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginRight: 4 }}>
          Office photos
        </div>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search name, market, or region…"
          testId="offices-search"
        />
        <ConsolidatedFilter
          dimensions={OFFICES_FILTER_DIMENSIONS}
          selections={selections}
          onChange={setSelections}
          testId="offices-filter-btn"
        />
        {isNonDefaultView && (
          <ClearButton onClick={handleClear} testId="offices-clear-btn" />
        )}
        <div style={{ marginLeft: "auto", fontSize: T.fsSm, color: T.mid }}>
          Click a card to upload a photo, or paste a URL.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 14,
        }}
      >
        {filtered.map((loc) => (
          <OfficeCard
            key={loc.id}
            location={loc}
            onUpload={handleUploadPhoto}
            onUrlUpdate={handleUrlUpdate}
          />
        ))}
      </div>
      {filtered.length === 0 && (
        <div style={{ color: T.light, textAlign: "center", padding: "40px 0", fontSize: T.fsBase }}>
          No offices match the current filters.
        </div>
      )}
    </div>
  );
}

function OfficeCard({ location, onUpload, onUrlUpdate }) {
  const [hovering, setHovering] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState(null);
  const fileRef = useRef(null);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    setErr(null);
    try {
      await onUpload(location, file);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  }

  async function handleUrlInput() {
    const url = window.prompt("Enter photo URL:");
    if (!url || !url.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      await onUrlUpdate(location, url.trim());
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid={`office-card-${location.id}`}
      style={{
        border: `0.5px solid ${T.border}`,
        borderRadius: 8,
        overflow: "hidden",
        background: T.card,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Photo area */}
      <div
        style={{
          position: "relative",
          height: 110,
          background: T.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          overflow: "hidden",
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={() => fileRef.current?.click()}
      >
        {location.imageUrl ? (
          <img
            src={location.imageUrl}
            alt={location.locationAlias || location.id}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div style={{ textAlign: "center", color: T.light }}>
            <div style={{ fontSize: T.fsXl6, marginBottom: 4 }}>📷</div>
            <div style={{ fontSize: T.fsXs }}>No photo</div>
          </div>
        )}
        {/* Hover overlay */}
        {hovering && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <span style={{ color: T.card, fontSize: T.fsSm, fontWeight: T.fwSemibold }}>
              {location.imageUrl ? "Change photo" : "Upload photo"}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleUrlInput(); }}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.4)",
                borderRadius: 4,
                color: T.card,
                fontSize: T.fsXs,
                padding: "2px 8px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              URL
            </button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      {/* Info */}
      <div style={{ padding: "10px 12px", flex: 1 }}>
        <div style={{ fontSize: T.fsXs, color: T.mid, fontFamily: "monospace", marginBottom: 2 }}>
          {location.id}
        </div>
        <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 3 }}>
          {location.locationAlias || "—"}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: T.fsXs, color: T.mid }}>{location.market || "—"}</span>
          <span
            style={{
              fontSize: T.fsXs,
              fontWeight: T.fwSemibold,
              color: T.blue,
              background: T.blueSoft,
              borderRadius: 4,
              padding: "1px 6px",
            }}
          >
            {getStageLabel(location.stage)}
          </span>
        </div>
        {err && <div style={{ fontSize: T.fsXs, color: T.red, marginTop: 4 }}>{err}</div>}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "8px 12px",
          borderTop: `0.5px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          minHeight: 18,
        }}
      >
        {saving && <span style={{ fontSize: T.fsXs, color: T.light }}>Saving…</span>}
      </div>
    </div>
  );
}

// ─── Tab 3: Location IDs ──────────────────────────────────────────────────────

const LOCATION_IDS_FILTER_DIMENSIONS = [
  { id: "region", label: "Region", options: REGIONS.map((r) => ({ value: r, label: r })) },
  { id: "status", label: "Status", options: [
    { value: "active",  label: "Active" },
    { value: "retired", label: "Retired" },
  ] },
];

function LocationIdsTab() {
  const [registry, setRegistry]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState("");
  const [selections, setSelections]   = useState({});

  const load = useCallback(() => {
    setLoading(true);
    apiFetch("/api/admin/location-ids")
      .then((reg) => {
        setRegistry(Array.isArray(reg) ? reg : []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const isNonDefaultView = search !== "" || !selectionsEqual(selections, {});

  function handleClear() {
    setSearch("");
    setSelections({});
  }

  // Filter registry — search is scoped to ID / city (alias) / market only.
  const filtered = registry.filter((r) => {
    const q = search.trim();
    if (q) {
      if (!matchesFoldedQuery(q, r.id, r.alias, r.market, r.marketName)) return false;
    }
    if (selections.region?.length > 0 && !selections.region.includes(r.region)) return false;
    if (selections.status?.length > 0) {
      const status = r.retiredAt ? "retired" : "active";
      if (!selections.status.includes(status)) return false;
    }
    return true;
  });

  if (loading) return <div style={{ color: T.mid, fontSize: T.fsSm }}>Loading location IDs…</div>;

  return (
    <div>
      {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}

      <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 14 }}>
        ID registry
      </div>

      {/* Toolbar — single line: search + standard filter control. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center" }}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search ID, city, or market…"
          testId="location-ids-search"
        />
        <ConsolidatedFilter
          dimensions={LOCATION_IDS_FILTER_DIMENSIONS}
          selections={selections}
          onChange={setSelections}
          testId="location-ids-filter-btn"
        />
        {isNonDefaultView && (
          <ClearButton onClick={handleClear} testId="location-ids-clear-btn" />
        )}
      </div>

      {/* Registry table */}
      <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${T.border}` }}>
            {["Location ID", "Region", "Market", "Alias", "Stage", "ID Status"].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  padding: "6px 10px",
                  color: T.mid,
                  fontWeight: T.fwSemibold,
                  fontSize: T.fsXs,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => (
            <tr
              key={row.id}
              data-testid={`registry-row-${row.id}`}
              style={{
                borderBottom: `0.5px solid ${T.border}`,
                opacity: row.retiredAt ? 0.5 : 1,
              }}
            >
              <td style={{ padding: "6px 10px", fontFamily: "monospace", fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold }}>
                {row.id}
              </td>
              <td style={{ padding: "6px 10px", color: T.mid }}>{row.region}</td>
              <td style={{ padding: "6px 10px", color: T.mid }}>{row.marketName || row.market || "—"}</td>
              <td style={{ padding: "6px 10px", color: T.dark }}>{row.alias || "—"}</td>
              <td style={{ padding: "6px 10px", color: T.mid }}>{row.reservation ? "—" : getStageLabel(row.stage)}</td>
              <td style={{ padding: "6px 10px" }}>
                {row.retiredAt ? (
                  <span style={{
                    fontSize: T.fsXs, color: T.mid, background: T.bg,
                    borderRadius: 4, padding: "2px 7px",
                  }}>
                    Retired
                    {row.supersededBy && <span> · Superseded by {row.supersededBy}</span>}
                  </span>
                ) : (
                  <span style={{
                    fontSize: T.fsXs, color: T.utilGreenText, background: T.utilGreen,
                    borderRadius: 4, padding: "2px 7px", fontWeight: T.fwSemibold,
                  }}>
                    Active
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {filtered.length === 0 && (
        <div style={{ color: T.light, textAlign: "center", padding: "32px 0", fontSize: T.fsBase }}>
          No location IDs match the current filters.
        </div>
      )}
    </div>
  );
}

// ─── Tab 4: Suggested next IDs ────────────────────────────────────────────────

const SUGGESTED_IDS_FILTER_DIMENSIONS = [
  { id: "region", label: "Region", options: REGIONS.map((r) => ({ value: r, label: r })) },
];

function SuggestedIdsTab() {
  const [nextSugg, setNextSugg]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [toast, setToast]           = useState(null);
  const [search, setSearch]         = useState("");
  const [selections, setSelections] = useState({});

  const isNonDefaultView = search !== "" || !selectionsEqual(selections, {});

  useEffect(() => {
    setLoading(true);
    getNextLocationIds()
      .then((next) => setNextSugg(Array.isArray(next) ? next : []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleClear() {
    setSearch("");
    setSelections({});
  }

  function handleCopy(text) {
    navigator.clipboard.writeText(text).then(() => showToast(`Copied: ${text}`));
  }

  if (loading) return <div style={{ color: T.mid, fontSize: T.fsSm }}>Loading suggested IDs…</div>;

  // Filter — search scoped to market / region / suggested ID; region filter
  // applied alongside it.
  const filtered = nextSugg.filter((mkt) => {
    const q = search.trim();
    if (q) {
      if (!matchesFoldedQuery(q, mkt.marketName, mkt.region, mkt.nextId)) return false;
    }
    if (selections.region?.length > 0 && !selections.region.includes(mkt.region)) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) =>
    (a.marketName || "").localeCompare(b.marketName || "", undefined, { sensitivity: "base" })
  );

  return (
    <div>
      {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}
      {toast && <ToastBanner message={toast} />}

      <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 14 }}>
        Suggested next IDs
      </div>

      {/* Toolbar — single line: search + standard filter control + Clear. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center" }}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search market, region, or suggested ID…"
          testId="suggested-ids-search"
        />
        <ConsolidatedFilter
          dimensions={SUGGESTED_IDS_FILTER_DIMENSIONS}
          selections={selections}
          onChange={setSelections}
          testId="suggested-ids-filter-btn"
        />
        {isNonDefaultView && (
          <ClearButton onClick={handleClear} testId="suggested-ids-clear-btn" />
        )}
      </div>

      {sorted.length === 0 ? (
        <div style={{ fontSize: T.fsSm, color: T.light }}>
          {nextSugg.length === 0 ? "No markets yet." : "No suggested IDs match the current search/filters."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {["Market", "Region", "Suggested next ID"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "6px 10px",
                    color: T.mid,
                    fontWeight: T.fwSemibold,
                    fontSize: T.fsXs,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((mkt) => (
              <tr
                key={mkt.marketId}
                data-testid={`next-id-row-${mkt.marketId}`}
                style={{ borderBottom: `0.5px solid ${T.border}` }}
              >
                <td style={{ padding: "6px 10px", fontWeight: T.fwSemibold, color: T.dark }}>{mkt.marketName}</td>
                <td style={{ padding: "6px 10px", color: T.mid }}>{mkt.region}</td>
                <td style={{ padding: "6px 10px" }}>
                  <span
                    style={{
                      fontSize: T.fsXs,
                      fontFamily: "monospace",
                      background: T.card,
                      border: `0.5px solid ${T.border}`,
                      borderRadius: 4,
                      padding: "2px 7px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    {mkt.nextId}
                    <button
                      type="button"
                      onClick={() => handleCopy(mkt.nextId)}
                      title="Copy to clipboard"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: T.light,
                        fontSize: T.fsXxs,
                        padding: 0,
                        lineHeight: 1,
                        fontFamily: "inherit",
                      }}
                    >
                      ⎘
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────

function MarketsMediaListOwner({ explainer } = {}) {
  const [initialTabState] = useState(readInitialTabState);
  const [activeTab, setActiveTab] = useState(initialTabState.tab);

  return (
    <div data-testid="admin-markets-media-page">
      <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
        Markets & Location IDs
      </div>
      {explainer}

      <TabBar active={activeTab} onChange={setActiveTab} />

      {activeTab === "markets"       && <MarketsTab />}
      {activeTab === "offices"       && <OfficesTab />}
      {activeTab === "id-registry"   && <LocationIdsTab />}
      {activeTab === "suggested-ids" && <SuggestedIdsTab />}
      {activeTab === "external-ids" && <ExternalIdsTab initialSource={initialTabState.source} />}
    </div>
  );
}
